import { Copy01 } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations';
import { RadioButton, RadioGroup } from '@/lib/base/radio-buttons';
import type { EmbedMode } from '@/lib/scheduling/buildEmbedSnippet';
import {
  buildEmbedSnippet,
  getTagOrigin,
} from '@/lib/scheduling/buildEmbedSnippet';
import { useEmbedCodeData } from '@/lib/scheduling/useEmbedCodeData';
import type { CrmObject } from '@/types/crm';

import { CollapsibleSection } from './collapsible-section';

const EMBED_MODE_OPTIONS: {
  value: EmbedMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'popup',
    label: 'Popup',
    description: 'Opens the booking form in a popup when a link is clicked.',
  },
  {
    value: 'floating',
    label: 'Floating button',
    description: 'Shows a floating "Book a meeting" button on the page.',
  },
];

interface EmbedCodeSectionProps {
  object: CrmObject;
}

export function EmbedCodeSection({ object }: EmbedCodeSectionProps) {
  const orgId = object.org_id;
  const entityId = object.entity_id;

  const [mode, setMode] = useState<EmbedMode>('popup');
  // Fetching is lazy: it stays disabled until the section is first expanded.
  const [hasExpanded, setHasExpanded] = useState(false);

  const { status, slug, bookingLink, retry } = useEmbedCodeData(
    orgId,
    entityId,
    hasExpanded
  );

  const handleExpandedChange = useCallback((expanded: boolean) => {
    if (expanded) {
      setHasExpanded(true);
    }
  }, []);

  const handleRetry = useCallback(() => {
    retry();
  }, [retry]);

  const handleModeChange = useCallback((v: string) => {
    setMode(v as EmbedMode);
  }, []);

  const snippet: string | null = useMemo(() => {
    if (status !== 'ready') {
      return null;
    }
    return buildEmbedSnippet({
      mode,
      tagOrigin: getTagOrigin(),
      slug,
      bookingLink,
    });
  }, [mode, status, slug, bookingLink]);

  const handleCopy = useCallback(async () => {
    if (!snippet) {
      return;
    }
    if (!navigator.clipboard?.writeText) {
      toast.error('Clipboard not available');
      return;
    }
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Embed code copied');
    } catch {
      toast.error("We couldn't copy the embed code. Try again.");
    }
  }, [snippet]);

  let body: ReactNode;

  if (status === 'idle') {
    body = null;
  } else if (status === 'loading') {
    body = (
      <div className='py-2' role='status' aria-busy='true'>
        <LoadingIndicator />
      </div>
    );
  } else if (status === 'unavailable') {
    body = (
      <p className='py-2 text-xs md:text-sm text-[var(--color-text-secondary)]'>
        This booking type doesn&apos;t have a shareable link yet.
      </p>
    );
  } else if (status === 'ready' && snippet) {
    body = (
      <div>
        <p className='text-xs md:text-sm text-[var(--color-text-secondary)]'>
          Add this snippet to your website to let visitors book this meeting.
        </p>
        <RadioGroup
          value={mode}
          onChange={handleModeChange}
          aria-label='Embed mode'
          className='mt-2'
        >
          {EMBED_MODE_OPTIONS.map(opt => (
            <RadioButton
              key={opt.value}
              value={opt.value}
              label={opt.label}
              description={opt.description}
            />
          ))}
        </RadioGroup>
        <pre
          aria-label='Embed code snippet'
          className='mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-[var(--color-bg-secondary)] p-3 text-xs font-mono text-[var(--color-text-primary)]'
        >
          {snippet}
        </pre>
        <Button
          color='secondary'
          size='sm'
          iconLeading={Copy01}
          onClick={handleCopy}
          className='mt-2'
        >
          Copy embed code
        </Button>
      </div>
    );
  } else {
    body = (
      <div
        role='alert'
        className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'
      >
        <p className='text-xs md:text-sm font-medium text-utility-error-800'>
          Couldn&apos;t load the embed code.
        </p>
        <Button
          color='link-destructive'
          size='sm'
          onClick={handleRetry}
          className='mt-1'
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <CollapsibleSection
      title='Embed code'
      defaultExpanded={false}
      onExpandedChange={handleExpandedChange}
    >
      {body}
    </CollapsibleSection>
  );
}
